import { useState, useRef, useEffect } from 'react';
import { CreationStatus, ApiClient } from '../api/client';
import { useNavigate } from 'react-router-dom';
import WebApp from '@twa-dev/sdk';
import { useTranslation } from '../i18n';

interface CreationItemProps {
  id: string;
  status: CreationStatus;
  strategyName: string;
  name?: string;
  createdAt: string;
  onClick: (id: string) => void;
  onDeleted?: () => void;
}

const statusColors: Record<CreationStatus, string> = {
  [CreationStatus.DRAFT]: 'bg-gray-500/30 text-gray-600 dark:bg-gray-400/20 dark:text-gray-300',
  [CreationStatus.PENDING]: 'bg-amber-500/20 text-amber-600 dark:bg-amber-400/20 dark:text-amber-300',
  [CreationStatus.WAITING_FOR_PAYMENT]: 'bg-purple-500/20 text-purple-600 dark:bg-purple-400/20 dark:text-purple-300',
  [CreationStatus.IN_PROGRESS]: 'bg-theme-button/20 text-theme-button dark:text-theme-button/90',
  [CreationStatus.COMPLETED]: 'bg-emerald-500/20 text-emerald-600 dark:bg-emerald-400/20 dark:text-emerald-300',
  [CreationStatus.FAILED]: 'bg-red-500/20 text-red-600 dark:bg-red-400/20 dark:text-red-300',
};

export function CreationItem({ id, status, strategyName, name, createdAt, onClick, onDeleted }: CreationItemProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { t } = useTranslation();

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return {
      date: date.toLocaleDateString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      }),
      time: date.toLocaleTimeString('de-DE', {
        hour: '2-digit',
        minute: '2-digit'
      })
    };
  };

  const displayStrategyName = t(`create.types.${strategyName}.header`, { defaultValue: strategyName });
  const formattedDateTime = formatDate(createdAt);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };

    if (menuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [menuOpen]);

  const handleItemClick = (e: React.MouseEvent) => {
    // Prevent the click from bubbling up to the parent div
    e.stopPropagation();
    onClick(id);
  };

  const handleMenuToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    setMenuOpen(!menuOpen);
  };

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    setMenuOpen(false);
    // Simply navigate to the creation details page
    navigate(`/creation/${id}`);
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    setMenuOpen(false);
    
    // Show a confirmation popup
    try {
      const tg = WebApp;
      // Show a simple confirmation popup
      tg.showPopup({
        title: t('creationDetails.deletePopup.title'),
        message: t('creationDetails.deletePopup.message'),
        buttons: [
          { type: 'cancel' },
          { 
            type: 'destructive', 
            text: t('creationDetails.deletePopup.deleteButton')
          }
        ]
      });
      
      // For simplicity, we'll just delete the creation directly
      // In a real app, you'd want to handle the popup result properly
      deleteCreation();
    } catch (error) {
      console.error('Failed to delete creation:', error);
      WebApp.showPopup({
        title: t('errors.errorTitle'),
        message: t('errors.deleteCreation'),
        buttons: [{ type: 'ok' }]
      });
    }
  };
  
  // Separate function to handle the actual deletion
  const deleteCreation = async () => {
    try {
      // Delete the creation
      await ApiClient.deleteCreation(id);
      
      // Notify parent component that this item was deleted
      if (onDeleted) {
        onDeleted();
      }
    } catch (error) {
      console.error('Failed to delete creation:', error);
      WebApp.showPopup({
        title: t('errors.errorTitle'),
        message: t('errors.deleteCreation'),
        buttons: [{ type: 'ok' }]
      });
    }
  };

  return (
    <div 
      onClick={handleItemClick}
      className="flex items-center gap-4 p-2 cursor-pointer"
    >
      {/* Thumbnail */}
      <div className="w-20 h-20 bg-black rounded-md flex items-center justify-center overflow-hidden">
        <span className="material-symbols-outlined text-white/50 text-3xl">movie</span>
      </div>
      
      {/* Content */}
      <div className="flex-1 min-w-0">
        <h3 className="text-theme-text font-medium truncate">
          {name || displayStrategyName}
        </h3>
        
        <div className="flex items-center text-theme-hint text-xs mt-1">
          <span>{formattedDateTime.date}</span>
          <span className="mx-1">•</span>
          <span>{formattedDateTime.time}</span>
        </div>
        
        <div className="flex items-center text-xs mt-1">
          <span className="text-theme-hint">1MB</span>
          <span className="mx-1 text-theme-hint">•</span>
          <span className="text-theme-hint">00:05</span>
          {status !== CreationStatus.COMPLETED && (
            <>
              <span className="mx-1 text-theme-hint">•</span>
              <span className={`${statusColors[status]} px-1 py-0.5 rounded`}>
                {t(`creationStatus.${status.toUpperCase()}`)}
              </span>
            </>
          )}
        </div>
      </div>
      
      {/* Options Menu */}
      <div className="relative" ref={menuRef}>
        <button 
          className="p-2 text-theme-hint hover:bg-black/5 dark:hover:bg-white/5 rounded-full"
          onClick={handleMenuToggle}
        >
          <span className="material-symbols-outlined">more_vert</span>
        </button>
        
        {menuOpen && (
          <div 
            className="absolute right-0 top-full mt-1 border border-black/10 dark:border-white/10 shadow-lg rounded-lg overflow-hidden z-10 min-w-[150px]"
            style={{ backgroundColor: 'var(--color-bg-primary)' }}
          >
            <button 
              className="w-full px-4 py-3 text-left text-sm text-theme-text hover:bg-black/5 dark:hover:bg-white/5 flex items-center gap-2"
              onClick={handleEdit}
            >
              <span className="material-symbols-outlined text-sm">edit</span>
              <span>{t('creationItem.editButton')}</span>
            </button>
            <div className="border-t border-black/10 dark:border-white/10"></div>
            <button 
              className="w-full px-4 py-3 text-left text-sm text-red-600 dark:text-red-400 hover:bg-black/5 dark:hover:bg-white/5 flex items-center gap-2"
              onClick={handleDelete}
            >
              <span className="material-symbols-outlined text-sm">delete</span>
              <span>{t('creationItem.deleteButton')}</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
} 